import React, { createContext, useContext, useReducer } from 'react';
import { toast } from 'sonner';
import { formatStock } from '../utils/presentations';

const CartContext = createContext();

// Helper function to generate unique cart item ID based on product ID,
// presentation (porción/individual/mediana/grande) and legacy variants.
const generateCartItemId = (productId, presentation, selectedVariants) => {
  const parts = [`${productId}`];
  if (presentation) parts.push(`p:${presentation}`);
  if (selectedVariants && Object.keys(selectedVariants).length > 0) {
    parts.push(JSON.stringify(selectedVariants));
  }
  return parts.join('_');
};

// Helper function to check if two items are the same (same product,
// same presentation and same variants). Combo-group items never merge:
// each combo instance is its own group with unique line ids.
const isSameItem = (item1, item2) => {
  if (item1.comboGroupId || item2.comboGroupId) return false;
  if (item1.id !== item2.id) return false;
  if ((item1.presentation || null) !== (item2.presentation || null)) return false;

  const variants1 = item1.selectedVariants || {};
  const variants2 = item2.selectedVariants || {};

  return JSON.stringify(variants1) === JSON.stringify(variants2);
};

// Build a fresh group id for a combo added/edited through the wizard.
/**
 * Unidades de contador que consume CADA pieza de una línea del carrito.
 * Lo calcula quien arma el payload (ProductCard, ComboWizard) porque ahí está
 * el `presentations` del producto; aquí sólo se lee. Una línea sin el campo
 * —carrito viejo, o un item legacy sin presentaciones— vale 1, que es el
 * comportamiento histórico y nunca sobrevende.
 */
export const unitsPerItem = (item) => Number(item?.unitsPerItem) || 1;

/** Unidades de contador ya reservadas de un producto, sumando TODAS sus líneas. */
const reservedUnitsFor = (items, productId) =>
  (items || []).reduce(
    (sum, ci) => (ci.id === productId ? sum + ci.quantity * unitsPerItem(ci) : sum),
    0
  );

const newComboGroupId = () =>
  `combo_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => isSameItem(item, action.payload));
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            isSameItem(item, action.payload)
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      }
      // Generate unique cart ID for new item
      const cartItemId = generateCartItemId(action.payload.id, action.payload.presentation, action.payload.selectedVariants);
      return {
        ...state,
        items: [...state.items, { ...action.payload, cartItemId, quantity: 1 }]
      };

    // Add a single presentation line with an explicit quantity (>=1).
    // Used by the ProductCard quantity picker; merges into an existing line.
    case 'ADD_ITEM_QTY': {
      const { item, count } = action.payload;
      if (!count || count <= 0) return state;
      const existing = state.items.find(ci => isSameItem(ci, item));
      if (existing) {
        return {
          ...state,
          items: state.items.map(ci =>
            isSameItem(ci, item)
              ? { ...ci, quantity: ci.quantity + count }
              : ci
          )
        };
      }
      const newCartItemId = generateCartItemId(item.id, item.presentation, item.selectedVariants);
      return {
        ...state,
        items: [...state.items, { ...item, cartItemId: newCartItemId, quantity: count }]
      };
    }

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.cartItemId !== action.payload)
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.cartItemId === action.payload.cartItemId
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };

    case 'ADD_COMBO_GROUP':
      return {
        ...state,
        items: [...state.items, ...action.payload.lineItems]
      };

    case 'REMOVE_COMBO_GROUP':
      return {
        ...state,
        items: state.items.filter(item => item.comboGroupId !== action.payload)
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  const addItem = (item) => {
    const existingItem = state.items.find(cartItem => isSameItem(cartItem, item));

    // El tope es del PRODUCTO y se mide en unidades de contador, no por
    // renglón ni por pieza: la porción y el grande del mismo platillo comen del
    // mismo inventario, y un grande de 800 g se lleva 4 porciones
    // (`unitsPerItem`, que calcula quien arma el payload). Contar renglones
    // dejaba pedir cuatro veces lo que hay.
    const porPieza = unitsPerItem(item);
    const reservado = reservedUnitsFor(state.items, item.id);
    if (reservado + porPieza > (Number(item.availability) || 0)) {
      toast.error(`No hay más ${item.title} disponibles`, {
        description: porPieza > 1 && reservado === 0
          ? 'No alcanza el inventario para ese tamaño'
          : `Ya reservaste lo que queda de este platillo`,
        duration: 3000,
      });
      return;
    }

    dispatch({ type: 'ADD_ITEM', payload: item });

    // Lo que queda se cuenta contra TODO lo reservado del producto y en
    // unidades de contador: antes se restaba la cantidad de este renglón al
    // stock total, así que dos renglones del mismo platillo anunciaban cada uno
    // el inventario completo.
    const restante = Math.max(0, (Number(item.availability) || 0) - reservado - porPieza);
    const cola = restante > 0 ? ` (quedan ${formatStock(restante)})` : ' (último disponible)';

    if (existingItem) {
      toast.success(`Se agregó otro ${item.title} al carrito`, {
        description: `Cantidad: ${existingItem.quantity + 1}${cola}`,
        duration: 3000,
      });
    } else {
      toast.success(`${item.title} agregado al carrito`, {
        description: `$${item.price}${cola}`,
        duration: 3000,
      });
    }
  };

  // Add several presentation lines of the SAME product at once, each with its
  // own quantity. `entries` = [{ item, count }]. Stock is clamped per line
  // (matching the existing per-line availability rule) and a single summary
  // toast is shown. Returns the total count actually added.
  const addItemsWithQty = (entries) => {
    let totalAdded = 0;
    let anyClamped = false;
    let lastTitle = '';

    entries.forEach(({ item, count }) => {
      if (!count || count <= 0) return;
      lastTitle = item.title;
      const stock = Number(item.availability) || 0;
      // Mismo criterio que addItem: lo que cabe se mide en unidades de
      // contador sobre TODO el producto, y cada pieza de esta línea consume
      // `unitsPerItem`. `existing` ya está incluido en el reservado.
      const porPieza = unitsPerItem(item);
      const libre = Math.max(0, stock - reservedUnitsFor(state.items, item.id));
      const allowed = Math.floor(libre / porPieza);
      const addCount = Math.min(count, allowed);
      if (addCount < count) anyClamped = true;
      if (addCount <= 0) return;
      dispatch({ type: 'ADD_ITEM_QTY', payload: { item, count: addCount } });
      totalAdded += addCount;
    });

    if (totalAdded > 0) {
      toast.success(
        totalAdded === 1
          ? `${lastTitle} agregado al carrito`
          : `${totalAdded} artículos agregados al carrito`,
        {
          description: anyClamped
            ? 'Algunos no se agregaron por falta de stock'
            : lastTitle,
          duration: 3000,
        }
      );
    } else if (anyClamped) {
      toast.error('No hay stock suficiente', { duration: 3000 });
    }

    return totalAdded;
  };

  const removeItem = (cartItemId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: cartItemId });
  };

  const updateQuantity = (cartItemId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { cartItemId, quantity } });
  };

  // Add a combo built in the wizard. `chosenItems` are the porción items
  // (1 per step). They share one comboGroupId and carry pricing in comboMeta.
  // `existingGroupId` replaces a group when editing.
  //
  // options.noDiscount → "arma tu plato": no combo discount. The group is
  // priced at the SUM of the porción prices (full price) and labelled with the
  // platillo name instead of the combo template name.
  const addComboGroup = (combo, chosenItems, existingGroupId = null, options = {}) => {
    const { noDiscount = false, displayName = null } = options;
    const groupId = existingGroupId || newComboGroupId();
    const individualSum = chosenItems.reduce((sum, it) => sum + (Number(it.price) || 0), 0);
    const effectivePrice = noDiscount ? individualSum : combo.price;
    const meta = {
      comboId: combo.id,
      name: displayName || combo.name,
      price: effectivePrice,
      categories: combo.categories,
      composition: combo.composition,
      noDiscount,
    };
    const lineItems = chosenItems.map((it, index) => ({
      ...it,
      quantity: 1,
      comboGroupId: groupId,
      comboStepIndex: index,
      comboMeta: meta,
      cartItemId: `${groupId}::${index}::${it.id}`,
    }));

    if (existingGroupId) {
      dispatch({ type: 'REMOVE_COMBO_GROUP', payload: existingGroupId });
    }
    dispatch({ type: 'ADD_COMBO_GROUP', payload: { lineItems } });

    toast.success(`${meta.name} ${existingGroupId ? 'actualizado' : 'agregado al carrito'}`, {
      description: noDiscount ? `Total: $${effectivePrice}` : `Precio combo: $${effectivePrice}`,
      duration: 3000,
    });
  };

  const removeComboGroup = (groupId) => {
    dispatch({ type: 'REMOVE_COMBO_GROUP', payload: groupId });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  // Items not belonging to any combo group (the à la carte path).
  const getLooseItems = () => state.items.filter(item => !item.comboGroupId);

  // Combo groups as [{ groupId, meta, items: [...] }], items in step order.
  const getComboGroups = () => {
    const groups = new Map();
    state.items.forEach(item => {
      if (!item.comboGroupId) return;
      if (!groups.has(item.comboGroupId)) {
        groups.set(item.comboGroupId, {
          groupId: item.comboGroupId,
          meta: item.comboMeta,
          items: [],
        });
      }
      groups.get(item.comboGroupId).items.push(item);
    });
    return Array.from(groups.values()).map(group => ({
      ...group,
      items: [...group.items].sort((a, b) => a.comboStepIndex - b.comboStepIndex),
    }));
  };

  // Sum of fixed combo prices (one per group).
  const getComboGroupsTotal = () =>
    getComboGroups().reduce((total, group) => total + (group.meta?.price || 0), 0);

  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  // Loose items at line price + combo groups at their fixed price.
  const getTotalPrice = () => {
    const looseTotal = getLooseItems().reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
    return looseTotal + getComboGroupsTotal();
  };

  return (
    <CartContext.Provider value={{
      items: state.items,
      addItem,
      addItemsWithQty,
      removeItem,
      updateQuantity,
      addComboGroup,
      removeComboGroup,
      getLooseItems,
      getComboGroups,
      getComboGroupsTotal,
      clearCart,
      getTotalItems,
      getTotalPrice
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
